import { useMemo } from 'react';
import { AttemptWithDetails } from '@/api/useAllUserPaperAttempts';

interface ProgressStats {
  totalPapers: number;
  completedPapers: number;
  completionPercentage: number;
  avgGrade: number;
  gradeImprovement: number;
  hasAttempts: boolean;
}

const useProgressStats = (attempts: AttemptWithDetails[], totalPapers: number): ProgressStats => {
  return useMemo(() => {
    const completedPapers = new Set(attempts.map((a) => a.past_paper_id)).size;
    const completionPercentage = totalPapers > 0 ? Math.round((completedPapers / totalPapers) * 100) : 0;

    // Calculate average grade from last 3 attempts only
    const last3Attempts = attempts.slice(-3);
    const totalGrade = last3Attempts.reduce((sum, a) => sum + (a.gradeOutOf9 || 0), 0);
    const avgGrade = last3Attempts.length > 0 ? totalGrade / last3Attempts.length : 0;

    // Calculate rolling average improvement (delta between current and previous 3-attempt averages)
    let gradeImprovement = 0;
    if (attempts.length >= 4) {
      // Current rolling average: last 3 attempts (N-2, N-1, N)
      const currentWindow = attempts.slice(-3);
      const currentAvg = currentWindow.reduce((sum, a) => sum + (a.gradeOutOf9 || 0), 0) / 3;

      // Previous rolling average: 3 attempts before that (N-3, N-2, N-1)
      const previousWindow = attempts.slice(-4, -1);
      const previousAvg = previousWindow.reduce((sum, a) => sum + (a.gradeOutOf9 || 0), 0) / 3;

      gradeImprovement = currentAvg - previousAvg;
    }

    return {
      totalPapers,
      completedPapers,
      completionPercentage,
      avgGrade: Math.round(avgGrade * 10) / 10,
      gradeImprovement: Math.round(gradeImprovement * 10) / 10,
      hasAttempts: attempts.length > 0,
    };
  }, [attempts, totalPapers]);
};

export default useProgressStats;
